import { execFileSync } from 'child_process';
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'fs';
import { dirname, join, relative, resolve, sep } from 'path';
import { OkraRuntimeError } from '../../errors';
import { Data, Effect } from 'effect';

export class ScaffoldCommandError extends Data.TaggedError('ScaffoldCommandError')<{
  readonly method: 'scaffoldPlugin';
  readonly status?: number;
}> {}

const scaffoldCommandErrorCauses = new WeakMap<ScaffoldCommandError, unknown>();

function scaffoldCommandFailure(cause: unknown): ScaffoldCommandError {
  const error = new ScaffoldCommandError({
    method: 'scaffoldPlugin',
    ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
  });
  scaffoldCommandErrorCauses.set(error, cause);
  return error;
}

export function scaffoldCommandErrorCause(error: ScaffoldCommandError): unknown {
  return scaffoldCommandErrorCauses.get(error);
}

export type ScaffoldRole = 'parser' | 'source' | 'channel' | 'publisher';

export interface ScaffoldPluginOptions {
  cwd?: string;
  force?: boolean;
  generate?: boolean;
}

export interface ScaffoldGeneratedArtifact {
  id: 'ecosystem-catalog-descriptors';
  path: string;
  cmd: string;
}

export interface ScaffoldPluginResult {
  id: string;
  role: ScaffoldRole;
  descriptorPath: string;
  packagePath: string;
  files: string[];
  generated: ScaffoldGeneratedArtifact[];
  catalog: {
    descriptorGenerated: boolean;
    generatedDescriptorPath: string;
    pluginListCommand: string;
    ecosystemPath: '/ecosystem';
    pluginPath: string;
  };
  next_actions: Array<{ cmd: string; why: string }>;
}

interface RolePlan {
  descriptorKind: 'parser' | 'source' | 'operation_pack';
  packageRole: 'parser' | 'channel' | 'publisher';
  sourceDir: 'parser' | 'channel' | 'publisher';
}

const ROLE_PLANS: Record<ScaffoldRole, RolePlan> = {
  parser: { descriptorKind: 'parser', packageRole: 'parser', sourceDir: 'parser' },
  source: { descriptorKind: 'source', packageRole: 'channel', sourceDir: 'channel' },
  channel: { descriptorKind: 'source', packageRole: 'channel', sourceDir: 'channel' },
  publisher: { descriptorKind: 'operation_pack', packageRole: 'publisher', sourceDir: 'publisher' },
};

const CATALOG_DESCRIPTOR_GENERATION_CMD =
  'node --experimental-strip-types packages/ecosystem-catalog/scripts/gen-plugin-descriptors.ts';
const CATALOG_DESCRIPTOR_GENERATED_PATH =
  'packages/ecosystem-catalog/src/plugin-descriptors.generated.ts';

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toTitle(value: string): string {
  return value
    .split('-')
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(' ');
}

function toPascal(value: string): string {
  return value
    .split('-')
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join('');
}

function normalizeRole(role: string): ScaffoldRole {
  const normalized = role.trim().toLowerCase().replace(/_/g, '-') as ScaffoldRole;
  if (normalized in ROLE_PLANS) return normalized;
  throw new OkraRuntimeError('INVALID_REQUEST', `Unsupported scaffold role "${role}"`, 400, {
    error: 'unsupported_scaffold_role',
    message: 'Use one of: parser, source, channel, publisher.',
    next_actions: [
      {
        cmd: 'okra plugin add parser mistral-ocr',
        why: 'Create a parser plugin descriptor and package stub.',
      },
      {
        cmd: 'okra plugin add source google-drive',
        why: 'Create a source connector descriptor and channel package stub.',
      },
    ],
  });
}

function normalizeName(name: string): string {
  const id = slugify(name);
  if (!/^[a-z0-9][a-z0-9-]*$/.test(id)) {
    throw new OkraRuntimeError('INVALID_REQUEST', `Invalid scaffold name "${name}"`, 400, {
      error: 'invalid_scaffold_name',
      message: 'Use lowercase letters, numbers, and hyphens.',
      next_actions: [
        { cmd: 'okra plugin add parser mistral-ocr', why: 'Use a URL-safe plugin id.' },
      ],
    });
  }
  return id;
}

function findRepoRoot(cwd: string): string {
  let dir = resolve(cwd);
  for (;;) {
    if (
      existsSync(join(dir, 'pnpm-workspace.yaml')) &&
      existsSync(join(dir, 'packages', 'ecosystem-catalog'))
    ) {
      return dir;
    }
    const parent = dirname(dir);
    if (parent === dir) {
      throw new OkraRuntimeError('INVALID_REQUEST', 'Could not find okra monorepo root', 400, {
        error: 'repo_root_not_found',
        message: 'Run this command from the okra monorepo, or pass --root <path>.',
        next_actions: [
          {
            cmd: 'okra plugin add parser mistral-ocr --root /path/to/okra',
            why: 'Point the scaffolder at the monorepo root.',
          },
        ],
      });
    }
    dir = parent;
  }
}

function catalogMetadata(packageName: string, packagePath: string): string {
  return `  "catalog": {
    "npm_package": "${packageName}",
    "package_path": "${packagePath}"
  },
`;
}

function parserDescriptor(
  id: string,
  title: string,
  packageName: string,
  packagePath: string,
): string {
  return `{
  // okra-plugin descriptor - parser preset over the generic openai_compatible adapter.
  "id": "${id}",
  "version": "0.1.0",
  "kind": "parser",
  "runtime": "openai_compatible",
  "format": "sandboxed",
  "trust": "external",
  "name": "${title}",
  "description": "TODO: describe what ${title} parses best and what data leaves the runtime.",
${catalogMetadata(packageName, packagePath)}  "category": "parse-extract",
  "tags": ["ocr", "vlm"],
  "publisher": "okrapdf",
  "license": "MIT",
  "capabilities": ["network:fetch"],
  "allowed_hosts": ["openrouter.ai"],
  "config_schema": {
    "apiKey": {
      "type": "string",
      "secret": true,
      "required": true,
      "label": "OpenRouter API key",
      "placeholder": "sk-or-..."
    }
  },
  "adapter": {
    "type": "openai_compatible",
    "base_url": "https://openrouter.ai/api/v1",
    "model": "TODO_PROVIDER/TODO_MODEL",
    "openrouter_pdf_mode": "native"
  }
}
`;
}

function sourceDescriptor(
  id: string,
  title: string,
  packageName: string,
  packagePath: string,
): string {
  return `{
  // okra-plugin descriptor - source connector blueprint.
  "id": "${id}",
  "version": "0.1.0",
  "kind": "source",
  "runtime": "http_base_url",
  "format": "sandboxed",
  "trust": "external",
  "name": "${title}",
  "description": "TODO: describe which PDFs ${title} imports and how credentials are scoped.",
${catalogMetadata(packageName, packagePath)}  "category": "sources",
  "publisher": "okrapdf",
  "license": "MIT",
  "capabilities": ["network:fetch", "source:list", "source:fetch"],
  "allowed_hosts": [],
  "allowed_hosts_from": "baseUrl",
  "config_schema": {
    "baseUrl": {
      "type": "string",
      "required": true,
      "label": "${title} API base URL"
    },
    "apiKey": {
      "type": "string",
      "secret": true,
      "required": true,
      "label": "${title} API key"
    }
  }
}
`;
}

function publisherDescriptor(
  id: string,
  title: string,
  packageName: string,
  packagePath: string,
): string {
  return `{
  // okra-plugin descriptor - publisher / operation-pack blueprint.
  "id": "${id}",
  "version": "0.1.0",
  "kind": "operation_pack",
  "runtime": "http_base_url",
  "format": "sandboxed",
  "trust": "external",
  "name": "${title}",
  "description": "TODO: describe the publish action and output artifact.",
${catalogMetadata(packageName, packagePath)}  "category": "advanced-workflow",
  "publisher": "okrapdf",
  "license": "MIT",
  "capabilities": ["network:fetch"],
  "allowed_hosts": [],
  "allowed_hosts_from": "baseUrl",
  "config_schema": {
    "baseUrl": {
      "type": "string",
      "required": true,
      "label": "${title} API base URL"
    },
    "apiKey": {
      "type": "string",
      "secret": true,
      "required": false,
      "label": "${title} API key"
    }
  },
  "adapter": {
    "type": "http_base_url",
    "base_url_from": "baseUrl",
    "auth": {
      "header": "Authorization",
      "value_from": "apiKey",
      "optional": true
    },
    "operations": [
      {
        "id": "run",
        "title": "Run ${title}",
        "category": "advanced-workflow",
        "method": "POST",
        "path": "/run",
        "request": "multipart/form-data",
        "fields": [{ "name": "file", "type": "file", "required": true }],
        "response": "application/json"
      }
    ]
  }
}
`;
}

function descriptorFor(
  plan: RolePlan,
  id: string,
  title: string,
  packageName: string,
  packagePath: string,
): string {
  if (plan.descriptorKind === 'parser')
    return parserDescriptor(id, title, packageName, packagePath);
  if (plan.descriptorKind === 'source')
    return sourceDescriptor(id, title, packageName, packagePath);
  return publisherDescriptor(id, title, packageName, packagePath);
}

function packageJsonFor(packageName: string): string {
  return `${JSON.stringify(
    {
      name: packageName,
      version: '0.1.0',
      type: 'module',
      exports: {
        '.': './src/index.ts',
      },
      scripts: {
        build: 'tsc --noEmit',
        typecheck: 'tsc --noEmit',
      },
      license: 'MIT',
      devDependencies: {
        typescript: '^5.8.0',
      },
    },
    null,
    2,
  )}\n`;
}

function packageReadmeFor(id: string, plan: RolePlan, title: string, packageName: string): string {
  return `# ${packageName}

${title} ${plan.sourceDir} package scaffolded by \`okra plugin add\`.

## Catalog

- Descriptor: \`packages/ecosystem-catalog/plugins/${id}/okra-plugin.jsonc\`
- Catalog info: \`okra plugin info ${id}\`
- Public page: \`/plugins/${id}\`
- Ecosystem page: \`/ecosystem\`

## Runtime Stub

The generated runtime class returns a structured \`status: "stub"\` result from
\`run(input)\`. Replace that placeholder with the real ${plan.sourceDir}
implementation after filling in the descriptor TODOs and policy fields.

## Checks

\`\`\`sh
pnpm --filter ${packageName} typecheck
pnpm --filter @okrapdf/ecosystem-catalog gen:descriptors
okra plugin info ${id}
\`\`\`
`;
}

function tsconfig(): string {
  return `${JSON.stringify(
    {
      compilerOptions: {
        target: 'ES2022',
        module: 'ESNext',
        moduleResolution: 'bundler',
        strict: true,
        noEmit: true,
        skipLibCheck: true,
      },
      include: ['src'],
    },
    null,
    2,
  )}\n`;
}

function indexStub(id: string, plan: RolePlan): string {
  return `export { ${toPascal(id)}${toPascal(plan.sourceDir)} } from './${plan.sourceDir}/${id}';
export const pluginId = '${id}';
`;
}

function sourceStub(id: string, plan: RolePlan, title: string): string {
  const className = `${toPascal(id)}${toPascal(plan.sourceDir)}`;
  const resultType = `${className}RunResult`;
  return `export interface ${resultType} {
  object: 'okra.scaffold.${plan.sourceDir}.run_result';
  plugin_id: '${id}';
  role: '${plan.sourceDir}';
  status: 'stub';
  message: string;
  input: unknown;
}

export class ${className} {
  readonly id = '${id}';
  readonly name = '${title}';

  async run(input: unknown = null): Promise<${resultType}> {
    return {
      object: 'okra.scaffold.${plan.sourceDir}.run_result',
      plugin_id: this.id,
      role: '${plan.sourceDir}',
      status: 'stub',
      message: 'TODO: implement ${id} ${plan.sourceDir} runtime',
      input,
    };
  }
}
`;
}

function ensureWritable(files: string[], force: boolean): void {
  if (force) return;
  const existing = files.filter((file) => existsSync(file));
  if (existing.length > 0) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      `Refusing to overwrite ${existing.length} existing scaffold file(s)`,
      400,
      {
        error: 'scaffold_target_exists',
        message: existing.join('\n'),
        next_actions: [
          {
            cmd: 'okra plugin add <role> <name> --force',
            why: 'Overwrite the existing scaffold files intentionally.',
          },
        ],
      },
    );
  }
}

function writeScaffoldFile(path: string, content: string): void {
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, content);
}

function outputText(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (value instanceof Uint8Array) {
    return new TextDecoder().decode(value).trim() || undefined;
  }
  return undefined;
}

function runCatalogDescriptorGeneration(repoRoot: string): ScaffoldGeneratedArtifact {
  const generatorPath = join(
    repoRoot,
    'packages',
    'ecosystem-catalog',
    'scripts',
    'gen-plugin-descriptors.ts',
  );
  if (!existsSync(generatorPath)) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      'Could not find ecosystem catalog descriptor generator',
      400,
      {
        error: 'descriptor_generator_not_found',
        message: `${relative(repoRoot, generatorPath)} does not exist.`,
        next_actions: [
          {
            cmd: CATALOG_DESCRIPTOR_GENERATION_CMD,
            why: 'Run descriptor generation after restoring the ecosystem catalog generator.',
          },
        ],
      },
    );
  }

  try {
    execFileSync(process.execPath, ['--experimental-strip-types', generatorPath], {
      cwd: repoRoot,
      encoding: 'utf8',
      stdio: 'pipe',
    });
  } catch (error) {
    const processError = error as { stdout?: unknown; stderr?: unknown; message?: string };
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      'Failed to regenerate ecosystem catalog descriptors',
      500,
      {
        error: 'descriptor_generation_failed',
        message:
          'The scaffolded files were written, but the generated catalog module was not refreshed.',
        cmd: CATALOG_DESCRIPTOR_GENERATION_CMD,
        stdout: outputText(processError.stdout),
        stderr: outputText(processError.stderr),
        cause: processError.message,
        next_actions: [
          {
            cmd: CATALOG_DESCRIPTOR_GENERATION_CMD,
            why: 'Rerun descriptor generation after fixing the reported descriptor or environment error.',
          },
        ],
      },
    );
  }

  return {
    id: 'ecosystem-catalog-descriptors',
    path: CATALOG_DESCRIPTOR_GENERATED_PATH,
    cmd: CATALOG_DESCRIPTOR_GENERATION_CMD,
  };
}

function generatedDescriptorIncludesPlugin(repoRoot: string, id: string): boolean {
  const generatedPath = join(repoRoot, CATALOG_DESCRIPTOR_GENERATED_PATH);
  if (!existsSync(generatedPath)) return false;
  const contents = readFileSync(generatedPath, 'utf8');
  return contents.includes(`"id": "${id}"`) || contents.includes(`id: "${id}"`);
}

function assertGeneratedDescriptorIncludesPlugin(repoRoot: string, id: string): void {
  if (generatedDescriptorIncludesPlugin(repoRoot, id)) return;
  throw new OkraRuntimeError(
    'INVALID_REQUEST',
    'Generated ecosystem catalog is missing the scaffolded descriptor',
    500,
    {
      error: 'descriptor_generation_missing_plugin',
      message: `${CATALOG_DESCRIPTOR_GENERATED_PATH} does not contain descriptor id "${id}".`,
      cmd: CATALOG_DESCRIPTOR_GENERATION_CMD,
      next_actions: [
        {
          cmd: CATALOG_DESCRIPTOR_GENERATION_CMD,
          why: 'Regenerate the descriptor module and confirm the new descriptor is valid JSONC.',
        },
        {
          cmd: `okra plugin info ${id}`,
          why: 'Confirm the descriptor resolves through the unified catalog after regeneration.',
        },
      ],
    },
  );
}

export const scaffoldPluginEffect = Effect.fn('okrapdf/cli.scaffoldPlugin')(
  (
    roleInput: string,
    nameInput: string,
    options: ScaffoldPluginOptions = {},
  ): Effect.Effect<ScaffoldPluginResult, ScaffoldCommandError> =>
    Effect.try({
      try: () => scaffoldPluginSync(roleInput, nameInput, options),
      catch: scaffoldCommandFailure,
    }),
);

export function scaffoldPlugin(
  roleInput: string,
  nameInput: string,
  options: ScaffoldPluginOptions = {},
): ScaffoldPluginResult {
  return Effect.runSync(
    scaffoldPluginEffect(roleInput, nameInput, options).pipe(
      Effect.catch((error) => Effect.fail(scaffoldCommandErrorCause(error))),
    ),
  );
}

function scaffoldPluginSync(
  roleInput: string,
  nameInput: string,
  options: ScaffoldPluginOptions,
): ScaffoldPluginResult {
  const role = normalizeRole(roleInput);
  const plan = ROLE_PLANS[role];
  const id = normalizeName(nameInput);
  const title = toTitle(id);
  const repoRoot = findRepoRoot(options.cwd ?? process.cwd());
  const repoPath = (file: string) => relative(repoRoot, file).split(sep).join('/');
  const descriptorPath = join(
    repoRoot,
    'packages',
    'ecosystem-catalog',
    'plugins',
    id,
    'okra-plugin.jsonc',
  );
  const packageDir = join(repoRoot, 'packages', `${plan.packageRole}-${id}`);
  const stubPath = join(packageDir, 'src', plan.sourceDir, `${id}.ts`);
  const indexPath = join(packageDir, 'src', 'index.ts');
  const packageJsonPath = join(packageDir, 'package.json');
  const readmePath = join(packageDir, 'README.md');
  const tsconfigPath = join(packageDir, 'tsconfig.json');
  const packageName = `@okrapdf/${plan.packageRole}-${id}`;
  const files = [descriptorPath, packageJsonPath, readmePath, tsconfigPath, indexPath, stubPath];

  ensureWritable(files, options.force === true);
  writeScaffoldFile(
    descriptorPath,
    descriptorFor(plan, id, title, packageName, repoPath(packageDir)),
  );
  writeScaffoldFile(packageJsonPath, packageJsonFor(packageName));
  writeScaffoldFile(readmePath, packageReadmeFor(id, plan, title, packageName));
  writeScaffoldFile(tsconfigPath, tsconfig());
  writeScaffoldFile(indexPath, indexStub(id, plan));
  writeScaffoldFile(stubPath, sourceStub(id, plan, title));

  const relativeFiles = files.map(repoPath);
  const generated = options.generate === false ? [] : [runCatalogDescriptorGeneration(repoRoot)];
  const descriptorGenerated =
    options.generate === false ? generatedDescriptorIncludesPlugin(repoRoot, id) : true;
  if (options.generate !== false) assertGeneratedDescriptorIncludesPlugin(repoRoot, id);
  const nextActions = [
    ...(options.generate === false
      ? [
          {
            cmd: 'pnpm --filter @okrapdf/ecosystem-catalog gen:descriptors',
            why: 'Regenerate the catalog descriptor module so /ecosystem and /plugins pick up the new descriptor.',
          },
        ]
      : []),
    {
      cmd: 'pnpm --filter @okrapdf/okra-solo gen:descriptors',
      why: 'Regenerate the okra-solo Worker descriptor module before running the plugin there.',
    },
    {
      cmd: `okra plugin info ${id}`,
      why: 'Confirm the scaffolded descriptor is visible through the unified catalog loader.',
    },
  ];

  return {
    id,
    role,
    descriptorPath: repoPath(descriptorPath),
    packagePath: repoPath(packageDir),
    files: relativeFiles,
    generated,
    catalog: {
      descriptorGenerated,
      generatedDescriptorPath: CATALOG_DESCRIPTOR_GENERATED_PATH,
      pluginListCommand: `okra plugin info ${id}`,
      ecosystemPath: '/ecosystem',
      pluginPath: `/plugins/${id}`,
    },
    next_actions: nextActions,
  };
}

export function formatScaffoldPluginResult(result: ScaffoldPluginResult): string {
  return [
    `Scaffolded ${result.role} plugin: ${result.id}`,
    '',
    `Descriptor: ${result.descriptorPath}`,
    `Package: ${result.packagePath}`,
    '',
    'Files:',
    ...result.files.map((file) => `  ${file}`),
    ...(result.generated.length > 0
      ? [
          '',
          'Generated:',
          ...result.generated.map((artifact) => `  ${artifact.path}  # ${artifact.cmd}`),
        ]
      : []),
    '',
    'Catalog:',
    `  ${result.catalog.pluginListCommand}`,
    `  ${result.catalog.ecosystemPath}`,
    `  ${result.catalog.pluginPath}`,
    `  descriptor generated: ${result.catalog.descriptorGenerated ? 'yes' : 'no'}`,
    '',
    'Next:',
    ...result.next_actions.map((action) => `  ${action.cmd}  # ${action.why}`),
  ].join('\n');
}
